import { Alert, Anchor, Badge, Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { IconPackage } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Offen', color: 'yellow' },
  AUTO_APPROVED: { label: 'Genehmigt — zu bestellen', color: 'teal' },
  APPROVED: { label: 'Genehmigt — zu bestellen', color: 'green' },
  REJECTED: { label: 'Abgelehnt', color: 'red' },
  OFFER_SENT: { label: 'Angebot gesendet', color: 'violet' },
  ORDERED: { label: 'Bestellt', color: 'blue' },
  COMPLETED: { label: 'Übernommen', color: 'gray' },
  CANCELLED: { label: 'Storniert', color: 'gray' },
};

const eur = (v: number) => v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const day = (s: string) => new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });

/** Materialanfrage in einer Bestell-Aufgabe — nur lesen (v1.407.0). Bestellt markiert ein Admin. */
export function AssignedRequestCard({ requestId }: { requestId: string }) {
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const q = useQuery({
    queryKey: ['assigned-technician-request', requestId],
    queryFn: () => api.getAssignedTechnicianRequest(requestId),
  });
  if (q.isLoading) return <Loader size="sm" />;
  if (q.isError || !q.data) return <Alert color="gray" variant="light">Materialanfrage nicht verfügbar.</Alert>;
  const r = q.data.request;
  const st = STATUS_LABEL[r.status] ?? { label: r.status, color: 'gray' };
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconPackage size={16} />
            <Text size="sm" fw={600}>{r.title}</Text>
          </Group>
          <Badge color={st.color} variant="light">{st.label}</Badge>
        </Group>
        <Text size="xs" c="dimmed">
          {r.user?.displayName ?? 'unbekannt'} · {r.category} · angefragt {day(r.createdAt)}
          {r.isInternal ? ' · intern' : ''}
        </Text>
        {r.amount != null && <Text size="sm">Betrag: <b>{eur(r.amount)}</b></Text>}
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{r.description}</Text>
        {r.orderedAt && (
          <Text size="xs" c="dimmed">Bestellt {day(r.orderedAt)}{r.orderedBy ? ` von ${r.orderedBy.displayName}` : ''}</Text>
        )}
        {r.expectedDeliveryAt && <Text size="xs" c="dimmed">Lieferung erwartet {day(r.expectedDeliveryAt)}</Text>}
        <Text size="xs" c="dimmed">
          Als bestellt markiert ein Admin — dann erledigt sich diese Aufgabe von selbst.
        </Text>
        {isAdmin && (
          <Anchor component={Link} to={`/admin/technician-requests?search=${encodeURIComponent(r.title)}`} size="xs">
            In den Materialanfragen öffnen →
          </Anchor>
        )}
      </Stack>
    </Paper>
  );
}
